import { type ApiCampaignRecord } from "@/services/campaigns.service";
import { type CampaignFormValues } from "./campaign-form";

export const defaultCampaignFormValues: CampaignFormValues = {
  brandId: "",
  brand: null,
  brandSearchQuery: "",
  minimumPrice: "",
  adFormat: "sponsored_products",
  campaignName: "",
  marketState: "",
  placementChannels: [],
  placementZones: [],
  budgetStartDate: "",
  budgetEndDate: "",
  totalBudget: "",
  pricingModel: "cpm",
  pacing: "none",
  dailyPacing: "",
};

function getRuleValue(
  rules: { dimension: string; values: string[] }[] | undefined,
  dimension: string,
) {
  return rules?.find((rule) => rule.dimension === dimension)?.values[0] ?? "";
}

function getPricingModel(type: string) {
  return type.toUpperCase() === "CPC" ? "cpc" : "cpm";
}

function getBudgetInputValue(budgetMilliDollars: number) {
  if (!budgetMilliDollars) {
    return "";
  }

  const budgetDollars = budgetMilliDollars / 1000;
  return Number.isInteger(budgetDollars)
    ? String(budgetDollars)
    : String(Number(budgetDollars.toFixed(2)));
}

function getDailyPacingInputValue(campaign: ApiCampaignRecord) {
  const dailyPacingRule = campaign.pacingRules.find(
    (rule) => rule.window === "DAY",
  );

  return dailyPacingRule ? getBudgetInputValue(dailyPacingRule.maxCredits) : "";
}

export function getCampaignBrandId(campaign: ApiCampaignRecord | undefined) {
  return getRuleValue(campaign?.productFilter?.rules, "BRAND");
}

export function getCloneCampaignFormValues(
  campaign: ApiCampaignRecord,
  brand: CampaignFormValues["brand"],
): CampaignFormValues {
  const productRules = campaign.productFilter?.rules;
  const eligibilityRules = campaign.eligibilityFilter?.rules;
  const brandId = getCampaignBrandId(campaign);
  const placementChannels = Array.from(
    new Set(
      campaign.adPlacements.map(({ adPlacement }) => adPlacement.channel),
    ),
  );

  return {
    ...defaultCampaignFormValues,
    brandId,
    brand,
    minimumPrice: getRuleValue(productRules, "PRICE"),
    campaignName: `Copy of ${campaign.name}`,
    marketState: getRuleValue(eligibilityRules, "STATE"),
    placementChannels,
    placementZones: campaign.adPlacements.map(
      ({ adPlacement }) => `${adPlacement.channel}.${adPlacement.zoneId}`,
    ),
    budgetStartDate: "",
    budgetEndDate: "",
    totalBudget: getBudgetInputValue(campaign.budget),
    pricingModel: getPricingModel(campaign.type),
    pacing: campaign.pacingRules.length > 0 ? "custom" : "none",
    dailyPacing: getDailyPacingInputValue(campaign),
  };
}
